// Pure helpers for the genome track viewer: which igv.js track a file becomes,
// which genome it is on, and where to open the browser. Kept free of igv.js so
// they are cheap to test.

export const TrackFormat = {
  Vcf: "vcf",
  Bed: "bed",
  BedGraph: "bedgraph",
  BedPe: "bedpe",
  Seg: "seg",
  Gff3: "gff3",
  Gtf: "gtf",
  BigWig: "bigwig",
} as const;
export type TrackFormat = (typeof TrackFormat)[keyof typeof TrackFormat];

// Text formats are not indexed here, so igv.js reads them whole (`indexed:
// false`); bigWig carries its own index and is read by range. One member per
// igv.js track kind, so the spec narrows to igv.js's own track options.
export type TrackSpec =
  | { type: "variant"; format: typeof TrackFormat.Vcf; indexed: false }
  | { type: "annotation"; format: typeof TrackFormat.Bed; indexed: false }
  | { type: "annotation"; format: typeof TrackFormat.Gff3; indexed: false }
  | { type: "annotation"; format: typeof TrackFormat.Gtf; indexed: false }
  | { type: "wig"; format: typeof TrackFormat.BedGraph; indexed: false }
  | { type: "interact"; format: typeof TrackFormat.BedPe; indexed: false }
  | { type: "seg"; format: typeof TrackFormat.Seg; indexed: false }
  | { type: "wig"; format: typeof TrackFormat.BigWig };

const TRACKS: readonly (readonly [readonly string[], TrackSpec])[] = [
  [[".vcf", ".vcf.gz"], { type: "variant", format: TrackFormat.Vcf, indexed: false }],
  [[".bed", ".bed.gz"], { type: "annotation", format: TrackFormat.Bed, indexed: false }],
  [[".bedgraph"], { type: "wig", format: TrackFormat.BedGraph, indexed: false }],
  [[".bedpe"], { type: "interact", format: TrackFormat.BedPe, indexed: false }],
  [[".seg"], { type: "seg", format: TrackFormat.Seg, indexed: false }],
  [[".gff3"], { type: "annotation", format: TrackFormat.Gff3, indexed: false }],
  [[".gtf"], { type: "annotation", format: TrackFormat.Gtf, indexed: false }],
  [[".bw", ".bigwig"], { type: "wig", format: TrackFormat.BigWig }],
];

export const GENOME_TRACK_SUFFIXES: readonly string[] = TRACKS.flatMap(([suffixes]) => suffixes);

export function trackFor(fileName: string): TrackSpec | null {
  const name = fileName.toLowerCase();
  const match = TRACKS.find(([suffixes]) => suffixes.some((suffix) => name.endsWith(suffix)));
  return match ? match[1] : null;
}

// The assembly a VCF header names, as an igv.js genome id. Only the GRCh37
// family needs detecting: everything else opens on the hg38 default.
export function genomeFromHeader(text: string): "hg38" | "hg19" | null {
  for (const line of text.split("\n")) {
    if (!line.startsWith("##")) {
      break;
    }
    // Only the reference and a contig's `assembly` name the build; a contig's
    // other fields (an md5 in particular) can contain these substrings.
    const lower = line.toLowerCase();
    const label = lower.startsWith("##reference=")
      ? lower.slice("##reference=".length)
      : lower.startsWith("##contig=")
        ? /[<,]assembly=([^,>]*)/.exec(lower)?.[1]
        : undefined;
    if (!label) {
      continue;
    }
    if (/grch37|hg19|b37|g1k_v37|hs37/.test(label)) {
      return "hg19";
    }
    if (/grch38|hg38|hs38/.test(label)) {
      return "hg38";
    }
  }
  return null;
}

const PADDING = 1_000;
const MAX_WINDOW = 1_000_000;
const MAX_RECORDS = 1_000;

interface Interval {
  chrom: string;
  start: number;
  end: number;
}

// The region to open on: the records of the file's first chromosome that lie
// within 1 Mb of its first record, padded by 1 kb. `null` when the text holds
// no parseable record, so igv.js opens on its default view.
export function initialLocus(text: string, format: TrackFormat): string | null {
  const records: Interval[] = [];
  let skippedSegHeader = false;
  for (const line of text.split("\n")) {
    if (records.length >= MAX_RECORDS) {
      break;
    }
    if (!line.trim() || line.startsWith("#") || /^(track|browser)\b/.test(line)) {
      continue;
    }
    if (format === TrackFormat.Seg && !skippedSegHeader) {
      skippedSegHeader = true;
      if (Number.isNaN(Number(line.split("\t")[2]))) {
        continue;
      }
    }
    const record = parseRecord(line.split("\t"), format);
    if (record) {
      records.push(record);
    }
  }
  const first = records[0];
  if (!first) {
    return null;
  }

  const limit = first.start + MAX_WINDOW - 1;
  let end = first.end;
  for (const record of records) {
    if (record.chrom === first.chrom && record.start <= limit) {
      end = Math.max(end, record.end);
    }
  }
  end = Math.min(end, limit);
  return `${first.chrom}:${Math.max(1, first.start - PADDING)}-${end + PADDING}`;
}

// 1-based, inclusive coordinates of one data line.
function parseRecord(fields: string[], format: TrackFormat): Interval | null {
  const int = (value: string | undefined) => (value && /^\d+$/.test(value) ? Number(value) : NaN);
  let chrom: string | undefined;
  let start: number;
  let end: number;
  switch (format) {
    case TrackFormat.Vcf: {
      chrom = fields[0];
      start = int(fields[1]);
      end = start + Math.max(0, (fields[3]?.length ?? 1) - 1);
      break;
    }
    case TrackFormat.Bed:
    case TrackFormat.BedGraph:
    case TrackFormat.BedPe: {
      chrom = fields[0];
      start = int(fields[1]) + 1;
      end = int(fields[2]);
      break;
    }
    case TrackFormat.Seg: {
      chrom = fields[1];
      start = int(fields[2]);
      end = int(fields[3]);
      break;
    }
    case TrackFormat.Gff3:
    case TrackFormat.Gtf: {
      chrom = fields[0];
      start = int(fields[3]);
      end = int(fields[4]);
      break;
    }
    case TrackFormat.BigWig:
      return null;
  }
  if (!chrom || Number.isNaN(start) || Number.isNaN(end) || end < start) {
    return null;
  }
  return { chrom, start, end };
}
